import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  businessesSelector,
  totalSelector,
  pageSelector,
  noMoreSelector,
  businessSelector,
} from './store/selector';
import { PutBusinessPayloadRequest } from './interfaces/put-business';
import { businessApiService } from './services/api';
import {
  getBusinessesInitApiAction,
  getBusinessesSuccessApiAction,
  getBusinessesFailureApiAction,
  postBusinessInitApiAction,
  postBusinessFailuireApiAction,
  putBusinessInitApiAction,
  putBusinessFailuireApiAction,
  deleteBusinessInitApiAction,
  deleteBusinessFailuireApiAction,
  setBusinessAction,
  increasePageAction,
  postBusinessSuccessApiAction,
  deleteBusinessSuccessApiAction,
  putBusinessSuccessApiAction,
  businessClearAction,
  postAreaInitApiAction,
  postAreaSuccessApiAction,
  postAreaFailureApiAction,
  putAreaInitApiAction,
  putAreaSuccessApiAction,
  putAreaFailureApiAction,
  deleteAreaInitApiAction,
  deleteAreaSuccessApiAction,
  deleteAreaFailureApiAction,
} from './store/reducer';
import { PostBusinessPayloadRequest } from './interfaces/post-business';
import { PostAreaRequest } from './interfaces/post-area';
import { PutAreaRequest } from './interfaces/put-area';
import { Business } from '../../interfaces/business.interface';

export function useBusinessCore() {
  const businesses = useSelector(businessesSelector);
  const business = useSelector(businessSelector);
  const total = useSelector(totalSelector);
  const page = useSelector(pageSelector);
  const noMore = useSelector(noMoreSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getBusinesses = useCallback(
    async (
      id: number,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getBusinessesInitApiAction());
        const response = await businessApiService.getBusinesses(id);
        dispatch(getBusinessesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getBusinessesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postBusiness = useCallback(
    async (
      id: number,
      body: PostBusinessPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postBusinessInitApiAction());
        await businessApiService.postBusiness(id, body);
        dispatch(postBusinessSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        const errorCode = error?.response?.status || error?.request?.status || 500;
        dispatch(
          postBusinessFailuireApiAction({ errorCode })
        );
      }
    },
    [dispatch]
  );

  const putBusiness = useCallback(
    async (
      cId: number,
      pId: number,
      body: PutBusinessPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putBusinessInitApiAction());
        await businessApiService.putBusiness(cId, pId, body);
        dispatch(putBusinessSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        const errorCode = error?.response?.status || error?.request?.status || 500;
        dispatch(
          putBusinessFailuireApiAction({ errorCode })
        );
      }
    },
    [dispatch]
  );

  const deleteBusiness = useCallback(
    async (cId: number, pId: number, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(deleteBusinessInitApiAction());
        await businessApiService.deleteBusiness(cId, pId);
        dispatch(deleteBusinessSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteBusinessFailuireApiAction());
      }
    },
    [dispatch]
  );

  const setBusiness = useCallback(
    (business: Business | null) => {
      dispatch(setBusinessAction(business));
    },
    [dispatch]
  );

  const increasePage = useCallback(() => {
    dispatch(increasePageAction());
  }, [dispatch]);

  const clearBusiness = useCallback(() => {
    dispatch(businessClearAction());
  }, [dispatch]);

  const postArea = useCallback(
    async (
      companyId: number,
      businessId: number,
      body: PostAreaRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postAreaInitApiAction());
        await businessApiService.postArea(companyId, businessId, body);
        dispatch(postAreaSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postAreaFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putArea = useCallback(
    async (
      companyId: number,
      businessId: number,
      areaId: number,
      body: PutAreaRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putAreaInitApiAction());
        await businessApiService.putArea(companyId, businessId, areaId, body);
        dispatch(putAreaSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          putAreaFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const deleteArea = useCallback(
    async (
      companyId: number,
      businessId: number,
      areaId: number,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(deleteAreaInitApiAction());
        await businessApiService.deleteArea(companyId, businessId, areaId);
        dispatch(deleteAreaSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          deleteAreaFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  return {
    businesses,
    business,
    total,
    page,
    noMore,
    errorCode,
    getBusinesses,
    postBusiness,
    putBusiness,
    deleteBusiness,
    setBusiness,
    increasePage,
    clearBusiness,
    postArea,
    putArea,
    deleteArea,
  };
}
